import { LIBRARY_REVIEWED, STANDARD_DISCLAIMER } from './taxonomy'
import type { RegulatoryRecord } from './types'

const reviewed = LIBRARY_REVIEWED
const disclaimer = STANDARD_DISCLAIMER

export const POLICY_AND_INTERNATIONAL_RECORDS: RegulatoryRecord[] = [
  {
    id: 'uae-net-zero-2050',
    slug: 'uae-net-zero-2050-strategy',
    title: 'UAE Net Zero 2050 Strategy',
    shortTitle: 'UAE Net Zero 2050 Strategy',
    authority: 'United Arab Emirates — coordinated by the Ministry of Climate Change and Environment',
    jurisdiction: ['federal'],
    categories: ['climate', 'energy'],
    instrumentType: 'policy',
    summary:
      'National strategy to reach net zero by 2050. The official government portal describes programmes across power, industry, transport, buildings, waste and agriculture, coordinated by the Ministry of Climate Change and Environment with a wide set of stakeholders. The Net Zero by 2050 strategic initiative was launched in October 2021. This record does not store a day-level launch date because the portal material reviewed states the month and year, not an exact day. The strategy is policy. It is not, by itself, a company greenhouse-gas filing obligation.',
    requirementsSummary: [
      'National net-zero direction for 2050, with sector programmes rather than a corporate return form.',
      'Ministry of Climate Change and Environment coordinates stakeholders.',
      'Companies may be affected through later laws, sector plans, customer requirements or state-owned-company mandates. Those have to be identified separately.',
    ],
    applicabilitySummary:
      'Informational for organisations operating in the UAE. It explains national direction. It does not designate a company as a reporting Source.',
    applicabilityConditions: ['No company-level filing trigger is stated in the portal page reviewed.'],
    practicalImplications: [
      'Useful context when setting a corporate climate target or explaining alignment with national policy.',
      'Do not cite the strategy as the legal basis for an annual emissions return. Use Federal Decree-Law No. 11 of 2024 and Cabinet Resolution No. 67 of 2024 for those questions.',
    ],
    recommendedActions: [
      'If the company sets a climate target, record whether it refers to this national strategy and keep that statement consistent with measured data.',
      'Track implementing laws separately in this navigator.',
    ],
    entityTypes: ['Organisations using national climate policy as context'],
    obligationLevel: 'informational',
    listingScope: 'not_specific',
    financialScope: 'not_specific',
    establishmentScope: 'not_specific',
    sectors: ['cross_sector'],
    deadlines: [
      {
        label: 'National net-zero aim of 2050. This is a national policy horizon, not a confirmed company filing date.',
        type: 'expected',
        certainty: 'expected',
        sourceUrl: 'https://u.ae/en/about-the-uae/strategies-initiatives-and-awards/strategies-plans-and-visions/environment-and-energy/the-uae-net-zero-2050-strategy',
      },
    ],
    status: 'in_force',
    officialSources: [
      {
        title: 'UAE Government portal — The UAE’s Net Zero 2050 Strategy',
        url: 'https://u.ae/en/about-the-uae/strategies-initiatives-and-awards/strategies-plans-and-visions/environment-and-energy/the-uae-net-zero-2050-strategy',
        sourceType: 'primary',
      },
    ],
    relatedRecordIds: ['uae-fdl-11-2024', 'uae-energy-strategy-2050'],
    relevantSections: ['Sector programmes', 'Role of the Ministry'],
    amendments: [],
    lastReviewedDate: reviewed,
    verificationStatus: 'verified',
    recordVersion: 1,
    changeHistory: [
      {
        date: reviewed,
        description: 'Initial navigator record. Classified as policy. No company deadline created from the 2050 horizon.',
        sourceUrl: 'https://u.ae/en/about-the-uae/strategies-initiatives-and-awards/strategies-plans-and-visions/environment-and-energy/the-uae-net-zero-2050-strategy',
      },
    ],
    disclaimer,
  },
  {
    id: 'uae-energy-strategy-2050',
    slug: 'uae-energy-strategy-2050',
    title: 'UAE Energy Strategy 2050',
    shortTitle: 'UAE Energy Strategy 2050',
    authority: 'United Arab Emirates Cabinet / national energy policy',
    jurisdiction: ['federal'],
    categories: ['energy', 'climate'],
    instrumentType: 'policy',
    summary:
      'National energy strategy, launched in 2017 and later updated. An official government document states that on 3 July 2023 the Cabinet adopted updated national energy and hydrogen strategies. The public strategy page describes aims including tripling renewable-energy contribution, investment of AED 150 to 200 billion, removing clean coal from the mix, and net zero in the electricity and water sector by 2050. Those are national targets. They are not a corporate renewable-energy purchase mandate or a company filing deadline.',
    requirementsSummary: [
      'National supply-and-demand strategy with 2030-period targets and 2050 ambitions described on the government portal.',
      '3 July 2023 is the Cabinet adoption date given in the official energy-strategy document hosted on u.ae for the update.',
      'No company reporting form is created by the strategy page reviewed.',
    ],
    applicabilitySummary: 'Informational context for energy users and energy companies. Legal duties, if any, come from licences, emirate utilities rules and other instruments, not from this strategy page alone.',
    applicabilityConditions: ['No corporate filing trigger was identified in the strategy materials reviewed.'],
    practicalImplications: [
      'Energy-intensive companies may be asked how their plans sit against the national strategy. That is a disclosure and planning question unless another instrument creates a duty.',
    ],
    recommendedActions: [
      'Keep corporate energy and renewable claims consistent with measured consumption.',
      'Check emirate utility and building rules separately. They are in the research queue until verified.',
    ],
    entityTypes: ['Energy-sector participants and large energy users using the strategy as context'],
    obligationLevel: 'informational',
    listingScope: 'not_specific',
    financialScope: 'not_specific',
    establishmentScope: 'not_specific',
    sectors: ['energy', 'cross_sector'],
    publicationDate: '2023-07-03',
    deadlines: [
      {
        date: '2023-07-03',
        label: 'Cabinet adoption date of the updated energy and hydrogen strategies, as stated in the official u.ae strategy document',
        type: 'effective',
        certainty: 'confirmed',
        sourceUrl: 'https://assets.u.ae/api/public/content/7d835b96ed924d22ac6915f41204ee86?v=85a7dd5a',
        notes: 'This is a policy adoption date, not a company compliance deadline. The original strategy launch year 2017 is not given a day in the page reviewed.',
      },
      {
        label: 'National ambition of net zero in the electricity and water sector by 2050. Not a company filing date.',
        type: 'expected',
        certainty: 'expected',
        sourceUrl: 'https://u.ae/en/about-the-uae/strategies-initiatives-and-awards/strategies-plans-and-visions/environment-and-energy/uae-energy-strategy-2050',
      },
    ],
    status: 'in_force',
    officialSources: [
      {
        title: 'UAE Government portal — UAE Energy Strategy 2050',
        url: 'https://u.ae/en/about-the-uae/strategies-initiatives-and-awards/strategies-plans-and-visions/environment-and-energy/uae-energy-strategy-2050',
        sourceType: 'primary',
      },
      {
        title: 'Official u.ae document — updated UAE Energy Strategy 2050',
        url: 'https://assets.u.ae/api/public/content/7d835b96ed924d22ac6915f41204ee86?v=85a7dd5a',
        sourceType: 'primary',
        relevantSection: 'Cabinet adoption on 3 July 2023',
      },
    ],
    relatedRecordIds: ['uae-net-zero-2050', 'uae-fdl-11-2024'],
    relevantSections: ['2030 targets', '2050 electricity and water ambition'],
    amendments: [
      {
        date: '2023-07-03',
        label: 'Updated strategy adopted by Cabinet on 3 July 2023, following the 2017 launch.',
        sourceUrl: 'https://assets.u.ae/api/public/content/7d835b96ed924d22ac6915f41204ee86?v=85a7dd5a',
      },
    ],
    lastReviewedDate: reviewed,
    verificationStatus: 'verified',
    recordVersion: 1,
    changeHistory: [
      {
        date: reviewed,
        description: 'Initial navigator record. 3 July 2023 stored as the documented Cabinet adoption date of the update. 2050 left as a national horizon.',
        sourceUrl: 'https://u.ae/en/about-the-uae/strategies-initiatives-and-awards/strategies-plans-and-visions/environment-and-energy/uae-energy-strategy-2050',
      },
    ],
    disclaimer,
  },
  {
    id: 'eu-cbam-2023-956',
    slug: 'eu-cbam-regulation-2023-956',
    title: 'EU Carbon Border Adjustment Mechanism — Regulation (EU) 2023/956',
    shortTitle: 'EU CBAM',
    authority: 'European Union',
    jurisdiction: ['international'],
    categories: ['cross_border', 'ghg', 'product_supply_chain'],
    instrumentType: 'international_instrument',
    summary:
      'EU regulation establishing a carbon border adjustment for specified imported goods. It is not UAE law. The definitive regime applies in the EU from 1 January 2026. Obligations to be authorised, report and surrender certificates sit with EU importers or their indirect customs representatives, not automatically with a UAE manufacturer. The regulation provides that the first annual CBAM declaration, for calendar year 2026, is submitted by 31 May 2027. UAE exporters of covered goods may be asked for embedded-emissions data. Product coverage, the current de minimis rule and verifier rules must be checked on the consolidated EU text because the regulation has been amended.',
    requirementsSummary: [
      'Not a UAE statute and not enforced by MOCCAE.',
      'Definitive regime from 1 January 2026, per the European Commission.',
      'Annual declaration by authorised CBAM declarants: by 31 May each year, first in 2027 for the year 2026, per the regulation.',
      'A UAE company’s role is usually that of a supplier of data to an EU importer, which is contractual unless the UAE company itself is the EU importer.',
      'Later EU amendments, including simplification measures described by the Commission, can change thresholds. This record does not freeze a tonne threshold as if it were unamended 2023 text.',
    ],
    applicabilitySummary:
      'Further verification is required if the company exports goods to the EU or sells to customers who import into the EU. Export alone does not prove that the product is a CBAM good or that the UAE company must file the EU declaration.',
    applicabilityConditions: [
      'Goods are in the current CBAM product scope.',
      'There is an importation into the EU customs territory.',
      'The filing obligation is on the authorised CBAM declarant in the EU.',
      'Current consolidated regulation and Commission guidance override any summary.',
    ],
    practicalImplications: [
      'Manufacturers of steel, aluminium, cement, fertilisers, electricity, hydrogen and other goods later brought into scope should expect data requests. Coverage still has to be checked on CN codes.',
      'The 31 May 2027 date is an EU declarant deadline. It is not a UAE federal filing date.',
    ],
    recommendedActions: [
      'List EU-bound products and ask customers which CN codes they import.',
      'Prepare activity data that can support embedded-emissions calculations if a covered product is confirmed.',
      'Read the Commission definitive-regime page and the consolidated regulation before quoting a threshold.',
    ],
    entityTypes: ['UAE producers or traders of goods that may be imported into the EU', 'UAE entities that are themselves EU importers'],
    obligationLevel: 'conditional',
    listingScope: 'not_specific',
    financialScope: 'not_specific',
    establishmentScope: 'not_specific',
    sectors: ['manufacturing', 'oil_gas', 'energy', 'construction'],
    publicationDate: '2023-05-10',
    effectiveDate: '2026-01-01',
    deadlines: [
      {
        date: '2023-05-10',
        label: 'Date of Regulation (EU) 2023/956',
        type: 'effective',
        certainty: 'confirmed',
        sourceUrl: 'https://eur-lex.europa.eu/eli/reg/2023/956/oj',
      },
      {
        date: '2026-01-01',
        label: 'Start of the CBAM definitive regime in the EU',
        type: 'effective',
        certainty: 'confirmed',
        sourceUrl: 'https://taxation-customs.ec.europa.eu/carbon-border-adjustment-mechanism_en',
        notes: 'EU regime start. Not a UAE filing date.',
      },
      {
        date: '2027-05-31',
        label: 'First annual CBAM declaration for calendar year 2026, due from authorised CBAM declarants',
        type: 'filing',
        certainty: 'confirmed',
        sourceUrl: 'https://eur-lex.europa.eu/eli/reg/2023/956/oj',
        notes: 'EU importer deadline. UAE suppliers may need to provide data earlier under contract. No UAE statutory filing day is created by this date.',
      },
    ],
    status: 'in_force',
    officialSources: [
      {
        title: 'EUR-Lex — Regulation (EU) 2023/956',
        url: 'https://eur-lex.europa.eu/eli/reg/2023/956/oj',
        sourceType: 'primary',
        relevantSection: 'Declaration timing, including 31 May 2027 for the 2026 year',
      },
      {
        title: 'European Commission — Carbon Border Adjustment Mechanism',
        url: 'https://taxation-customs.ec.europa.eu/carbon-border-adjustment-mechanism_en',
        sourceType: 'primary',
        relevantSection: 'Definitive regime from 1 January 2026',
      },
      {
        title: 'European Commission — CBAM definitive regime',
        url: 'https://taxation-customs.ec.europa.eu/carbon-border-adjustment-mechanism/cbam-definitive-regime_en',
        sourceType: 'primary',
        relevantSection: 'Current operator obligations; check for amended thresholds',
      },
    ],
    relatedRecordIds: ['ghg-protocol-corporate', 'uae-cab-67-2024'],
    relevantSections: ['Declarant obligations', 'Annual declaration timing'],
    amendments: [
      {
        label: 'EUR-Lex records that the act has been amended. Use the current consolidated version. Threshold changes are not restated as fixed numbers in this record.',
        sourceUrl: 'https://eur-lex.europa.eu/eli/reg/2023/956/oj',
      },
    ],
    lastReviewedDate: reviewed,
    verificationStatus: 'verified',
    recordVersion: 1,
    changeHistory: [
      {
        date: reviewed,
        description: 'Initial navigator record. Dates limited to the regulation date, 1 January 2026 regime start, and 31 May 2027 declarant deadline. Labelled as EU-side duties.',
        sourceUrl: 'https://eur-lex.europa.eu/eli/reg/2023/956/oj',
      },
    ],
    disclaimer,
  },
  {
    id: 'paris-agreement',
    slug: 'paris-agreement',
    title: 'Paris Agreement under the UNFCCC',
    shortTitle: 'Paris Agreement',
    authority: 'Parties to the United Nations Framework Convention on Climate Change',
    jurisdiction: ['international'],
    categories: ['climate', 'cross_border'],
    instrumentType: 'international_instrument',
    summary:
      'International climate treaty. It creates obligations between Parties, including nationally determined contributions. It does not itself impose a corporate GHG filing duty in the UAE. CBUAE Regulation C 8/2025 states that the UAE ratified the Paris Agreement in 2016. The federal decree number for that ratification was not verified in this review and is not stated. Federal Decree-Law No. 11 of 2024 is the domestic framework that refers to NDCs and international reporting by the State.',
    requirementsSummary: [
      'Party-level mitigation and adaptation architecture, including NDCs.',
      'Corporate duties in the UAE, where they exist, are in domestic instruments such as Federal Decree-Law No. 11 of 2024 and Cabinet Resolution No. 67 of 2024.',
      'The UNFCCC process is the primary international source. Domestic ratification details remain in the research queue.',
    ],
    applicabilitySummary: 'Informational context for UAE climate policy. Not a company compliance calendar.',
    applicabilityConditions: ['No corporate trigger is applied from the treaty alone.'],
    practicalImplications: ['Teams may refer to the Paris Agreement when explaining national targets. The operational question remains which domestic rule applies.'],
    recommendedActions: ['Use domestic legislation for company duties.', 'If a disclosure cites the Paris Agreement, keep the wording at national-policy level unless a corporate target has actually been adopted.'],
    entityTypes: ['Context for UAE organisations explaining climate policy'],
    obligationLevel: 'informational',
    listingScope: 'not_specific',
    financialScope: 'not_specific',
    establishmentScope: 'not_specific',
    sectors: ['cross_sector'],
    publicationDate: '2015-12-12',
    effectiveDate: '2016-11-04',
    deadlines: [
      {
        date: '2015-12-12',
        label: 'Adoption of the Paris Agreement',
        type: 'effective',
        certainty: 'confirmed',
        sourceUrl: 'https://unfccc.int/process-and-meetings/the-paris-agreement',
      },
      {
        date: '2016-11-04',
        label: 'Entry into force of the Paris Agreement',
        type: 'effective',
        certainty: 'confirmed',
        sourceUrl: 'https://unfccc.int/process-and-meetings/the-paris-agreement',
        notes: 'Treaty entry into force. Not a UAE company filing date. UAE ratification instrument number was not verified here.',
      },
    ],
    status: 'in_force',
    officialSources: [
      {
        title: 'UNFCCC — The Paris Agreement',
        url: 'https://unfccc.int/process-and-meetings/the-paris-agreement',
        sourceType: 'primary',
      },
      {
        title: 'CBUAE Regulation C 8/2025 — statement that the UAE ratified the Paris Agreement in 2016',
        url: 'https://rulebook.centralbank.ae/en/node/6431',
        sourceType: 'secondary',
        relevantSection: 'Introduction, ratification year only',
      },
    ],
    relatedRecordIds: ['uae-fdl-11-2024', 'uae-net-zero-2050'],
    relevantSections: ['Nationally determined contributions'],
    amendments: [],
    lastReviewedDate: reviewed,
    verificationStatus: 'verified',
    recordVersion: 1,
    changeHistory: [
      {
        date: reviewed,
        description: 'Initial navigator record. Domestic ratification decree left unverified.',
        sourceUrl: 'https://unfccc.int/process-and-meetings/the-paris-agreement',
      },
    ],
    disclaimer,
  },
  {
    id: 'ghg-protocol-corporate',
    slug: 'ghg-protocol-corporate-standard',
    title: 'GHG Protocol Corporate Accounting and Reporting Standard',
    shortTitle: 'GHG Protocol Corporate Standard',
    authority: 'World Resources Institute and World Business Council for Sustainable Development',
    jurisdiction: ['international'],
    categories: ['ghg', 'climate'],
    instrumentType: 'voluntary_framework',
    summary:
      'Widely used voluntary methodology for corporate greenhouse-gas inventories, including organisational boundaries and Scopes 1, 2 and 3. It is not UAE legislation. Cabinet Resolution No. 67 of 2024 refers to a greenhouse-gas inventory protocol and to ISO verification standards for in-scope entities. ADX guidance points issuers to the WRI/WBCSD GHG Protocol for emissions metrics. Using the Protocol does not by itself mean a company has been designated under Federal Decree-Law No. 11 of 2024.',
    requirementsSummary: [
      'Voluntary methodology unless a law, exchange rule or contract incorporates it.',
      'Defines approaches to consolidation and to direct and indirect emissions.',
      'No UAE filing date is created by the Protocol.',
    ],
    applicabilitySummary: 'A voluntary or market-driven reference when the company measures emissions, responds to customers, or falls under an instrument that points to inventory practice.',
    applicabilityConditions: ['Adoption is a methodology choice unless another instrument requires a specified standard.'],
    practicalImplications: ['A structured inventory is the practical way to test the 0.5 million tCO2e threshold in Cabinet Resolution No. 67 of 2024.', 'Missing data must not be treated as zero.'],
    recommendedActions: ['Document organisational boundary, scopes and data owners before publishing a total.', 'Keep the inventory separate from any legal conclusion about designation.'],
    entityTypes: ['Companies preparing a GHG inventory'],
    obligationLevel: 'voluntary',
    listingScope: 'not_specific',
    financialScope: 'not_specific',
    establishmentScope: 'not_specific',
    sectors: ['cross_sector'],
    deadlines: [],
    status: 'in_force',
    officialSources: [
      {
        title: 'GHG Protocol',
        url: 'https://ghgprotocol.org/',
        sourceType: 'primary',
      },
      {
        title: 'GHG Protocol Corporate Standard',
        url: 'https://ghgprotocol.org/corporate-standard',
        sourceType: 'primary',
      },
    ],
    relatedRecordIds: ['uae-fdl-11-2024', 'uae-cab-67-2024', 'adx-esg-guidance'],
    relevantSections: ['Corporate Standard', 'Scopes'],
    amendments: [],
    lastReviewedDate: reviewed,
    verificationStatus: 'verified',
    recordVersion: 1,
    changeHistory: [
      {
        date: reviewed,
        description: 'Initial navigator record. Classified as a voluntary methodology. No edition date invented.',
        sourceUrl: 'https://ghgprotocol.org/corporate-standard',
      },
    ],
    disclaimer,
  },
  {
    id: 'gri-standards',
    slug: 'gri-standards',
    title: 'GRI Standards',
    shortTitle: 'GRI Standards',
    authority: 'Global Reporting Initiative',
    jurisdiction: ['international'],
    categories: ['esg_disclosure'],
    instrumentType: 'voluntary_framework',
    summary:
      'Voluntary sustainability reporting standards. They are not UAE law. ADX maps parts of its metric guidance to GRI. A company reports under GRI only if it chooses to, or if a contract, parent or exchange expectation requires that choice. No UAE effective date applies.',
    requirementsSummary: ['Voluntary framework.', 'Not a UAE filing obligation.', 'Often referenced by exchange guidance and by reporters.'],
    applicabilitySummary: 'Market-driven when the company, its parent, its customers or its exchange materials use GRI.',
    applicabilityConditions: ['A decision or request to use GRI exists. Listing alone does not prove a GRI requirement.'],
    practicalImplications: ['Select topics from the company’s material impacts rather than attempting to fill every GRI disclosure by default.'],
    recommendedActions: ['Confirm whether any report the company already issues claims to be in accordance with GRI.', 'If it does, align evidence with that claim. If it does not, do not imply GRI compliance.'],
    entityTypes: ['Companies that adopt or are asked for GRI reporting'],
    obligationLevel: 'voluntary',
    listingScope: 'not_specific',
    financialScope: 'not_specific',
    establishmentScope: 'not_specific',
    sectors: ['cross_sector'],
    deadlines: [],
    status: 'in_force',
    officialSources: [
      {
        title: 'GRI Standards',
        url: 'https://www.globalreporting.org/standards/',
        sourceType: 'primary',
      },
    ],
    relatedRecordIds: ['adx-esg-guidance', 'dfm-esg-guidance'],
    relevantSections: ['GRI Standards'],
    amendments: [],
    lastReviewedDate: reviewed,
    verificationStatus: 'verified',
    recordVersion: 1,
    changeHistory: [
      {
        date: reviewed,
        description: 'Initial navigator record. No UAE effective date, because none exists for this voluntary framework.',
        sourceUrl: 'https://www.globalreporting.org/standards/',
      },
    ],
    disclaimer,
  },
  {
    id: 'ifrs-s1-s2',
    slug: 'ifrs-sustainability-disclosure-standards',
    title: 'IFRS Sustainability Disclosure Standards (IFRS S1 and IFRS S2)',
    shortTitle: 'IFRS S1 and IFRS S2',
    authority: 'International Sustainability Standards Board (IFRS Foundation)',
    jurisdiction: ['international'],
    categories: ['esg_disclosure', 'climate'],
    instrumentType: 'voluntary_framework',
    summary:
      'Investor-focused sustainability and climate disclosure standards issued by the ISSB on 26 June 2023. They are not automatically UAE law. They become mandatory only if a jurisdiction, a regulator, an exchange or a parent requires them. ADX climate metrics refer to IFRS S2 topics. CBUAE disclosure principles cover similar governance, strategy, risk and metrics themes for reporting entities, but that is a separate instrument. No UAE adoption decree was verified for this record, so no UAE effective date is stored.',
    requirementsSummary: [
      'IFRS S1 is the general sustainability disclosure standard. IFRS S2 is the climate standard.',
      'ISSB issue date 26 June 2023.',
      'UAE mandatory application was not verified and is not asserted.',
    ],
    applicabilitySummary: 'Voluntary or market-driven unless a verified UAE or home-state adoption decision, listing rule or group policy applies. “Not sure” on listing or parent requirements should stay in further verification, not in a conclusion of non-applicability.',
    applicabilityConditions: ['Jurisdiction adoption, regulator requirement, or a group decision to apply the standards.', 'No such UAE adoption instrument is cited in this record.'],
    practicalImplications: ['Useful architecture for climate governance and metrics, especially for listed issuers and groups with international parents.', 'Do not describe IFRS S2 as the UAE climate law.'],
    recommendedActions: ['Ask finance and company secretary whether any reporting mandate or group policy names IFRS S1 or S2.', 'If ADX metrics are in use, note where they already point to IFRS S2 language.'],
    entityTypes: ['Companies considering or required by another regime to use ISSB standards'],
    obligationLevel: 'voluntary',
    listingScope: 'not_specific',
    financialScope: 'not_specific',
    establishmentScope: 'not_specific',
    sectors: ['cross_sector'],
    publicationDate: '2023-06-26',
    deadlines: [
      {
        date: '2023-06-26',
        label: 'ISSB issuance of IFRS S1 and IFRS S2',
        type: 'effective',
        certainty: 'confirmed',
        sourceUrl: 'https://www.ifrs.org/issued-standards/ifrs-sustainability-standards-navigator/',
        notes: 'Issuance by the ISSB. Not a UAE effective date. No UAE filing deadline identified.',
      },
    ],
    status: 'in_force',
    officialSources: [
      {
        title: 'IFRS — Sustainability Standards Navigator',
        url: 'https://www.ifrs.org/issued-standards/ifrs-sustainability-standards-navigator/',
        sourceType: 'primary',
      },
    ],
    relatedRecordIds: ['adx-esg-guidance', 'cbuae-sustainability-disclosure-2024'],
    relevantSections: ['IFRS S1', 'IFRS S2'],
    amendments: [],
    lastReviewedDate: reviewed,
    verificationStatus: 'verified',
    recordVersion: 1,
    changeHistory: [
      {
        date: reviewed,
        description: 'Initial navigator record. UAE adoption left unverified, so no domestic effective date is stored.',
        sourceUrl: 'https://www.ifrs.org/issued-standards/ifrs-sustainability-standards-navigator/',
      },
    ],
    disclaimer,
  },
  {
    id: 'market-esg-requests',
    slug: 'customer-lender-esg-information-requests',
    title: 'Customer, lender and investor sustainability information requests',
    shortTitle: 'Customer and lender ESG requests',
    authority: 'Contractual counterparties — not a UAE regulator',
    jurisdiction: ['international'],
    categories: ['product_supply_chain', 'cross_border', 'esg_disclosure'],
    instrumentType: 'market_expectation',
    summary:
      'There is no single UAE statute that requires a company to answer a customer carbon questionnaire or a bank ESG due-diligence pack. Those requests are contractual or commercial. They often arrive before any federal designation under the climate Decree-Law. This record exists so the navigator can show that distinction. It is not an official legal source.',
    requirementsSummary: [
      'Not legislation.',
      'Scope, frequency and evidence depend on the contract, tender or facility agreement.',
      'A questionnaire is not proof of a statutory filing duty.',
    ],
    applicabilitySummary: 'Market-driven when customers, lenders or investors have asked for emissions or ESG information, or the company expects those requests.',
    applicabilityConditions: ['A real or anticipated counterparty request exists.', 'The legal effect, if any, is in the contract, which this tool does not interpret.'],
    practicalImplications: ['Teams need an evidence pack even when no statutory return is due.', 'Software can organise data. It does not decide whether a contract clause is binding.'],
    recommendedActions: ['Keep a register of ESG information requests and the clause or email that created each one.', 'Answer from measured data and label estimates.', 'Take legal questions about contract terms to counsel.'],
    entityTypes: ['Suppliers to customers with ESG programmes', 'Borrowers facing sustainability due diligence'],
    obligationLevel: 'voluntary',
    listingScope: 'not_specific',
    financialScope: 'not_specific',
    establishmentScope: 'not_specific',
    sectors: ['cross_sector'],
    deadlines: [],
    status: 'in_force',
    officialSources: [
      {
        title: 'Ecopshub navigator methodology — market requests are not statutes',
        url: 'https://ecopshub.com/uae-sustainability-regulations/methodology',
        sourceType: 'secondary',
        relevantSection: 'Market-driven expectations',
      },
    ],
    relatedRecordIds: ['ghg-protocol-corporate', 'eu-cbam-2023-956'],
    relevantSections: ['Not applicable — this is not a statute'],
    amendments: [],
    lastReviewedDate: reviewed,
    verificationStatus: 'verified',
    recordVersion: 1,
    changeHistory: [
      {
        date: reviewed,
        description: 'Initial editorial record so market requests are not mislabelled as UAE law.',
        sourceUrl: 'https://ecopshub.com/uae-sustainability-regulations/methodology',
      },
    ],
    disclaimer,
  },
]
